import { Controller } from "@hotwired/stimulus"

const SDK_ID = "featurebase-sdk"

let sdkPromise = null
let identifiedJwt = null

// Vitest helper — module state survives across examples otherwise.
export function __resetFeaturebaseControllerStateForTests() {
  sdkPromise = null
  identifiedJwt = null
}

export default class extends Controller {
  static targets = ["embed", "error"]

  static values = {
    organization: String,
    jwt: String,
    theme: String,
    sdkUrl: String,
    embedPath: { type: String, default: "/" },
  }

  connect() {
    this.mount()
  }

  disconnect() {
    // Allow remount after Turbo navigates away and back to /feedback.
    this.embedInitialized = false
    this.clearEmbedTarget()
  }

  async mount() {
    if (this.mounting) return

    this.mounting = true
    try {
      await this.ensureSdk()
      // Script load can outlive this Stimulus instance (Turbo navigation).
      if (!this.element.isConnected) return

      this.identify()
      this.initEmbed()
    } catch (error) {
      console.error("[featurebase] embed failed to load", error)
      this.showError()
    } finally {
      this.mounting = false
    }
  }

  retry(event) {
    event?.preventDefault()
    this.hideError()
    this.mount()
  }

  showError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = false
    if (this.hasEmbedTarget) this.embedTarget.hidden = true
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
    if (this.hasEmbedTarget) this.embedTarget.hidden = false
  }

  ensureSdk() {
    this.ensureQueue()

    if (sdkPromise) return sdkPromise

    const existing = document.getElementById(SDK_ID)
    if (existing) {
      sdkPromise = Promise.resolve()
      return sdkPromise
    }

    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.id = SDK_ID
      script.async = true
      script.addEventListener("load", () => resolve(), { once: true })
      script.addEventListener(
        "error",
        () => {
          script.remove()
          sdkPromise = null
          reject(new Error("Featurebase SDK failed to load"))
        },
        { once: true },
      )
      document.head.appendChild(script)
      script.src = this.sdkUrlValue
    })

    return sdkPromise
  }

  ensureQueue() {
    if (typeof window.Featurebase !== "function") {
      window.Featurebase = function () {
        ;(window.Featurebase.q = window.Featurebase.q || []).push(arguments)
      }
    }
  }

  identify() {
    const jwt = this.jwtValue
    if (!jwt || jwt === identifiedJwt) return

    // identify — not boot. boot mounts Featurebase Messenger (CORE-9260).
    window.Featurebase("identify", {
      organization: this.organizationValue,
      featurebaseJwt: jwt,
    })
    identifiedJwt = jwt
  }

  initEmbed() {
    if (this.embedInitialized) return

    window.Featurebase("init_embed_widget", {
      organization: this.organizationValue,
      featurebaseJwt: this.jwtValue,
      embedOptions: {
        path: this.embedPathValue || "/",
        filters: "",
        routeSyncingBasePath: "/feedback",
      },
      stylingOptions: {
        theme: this.themeValue || "light",
        hideMenu: false,
        hideLogo: false,
      },
    })
    this.embedInitialized = true
  }

  clearEmbedTarget() {
    this.element.querySelector("[data-featurebase-embed]")?.replaceChildren()
  }
}
